import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  DocumentIcon,
  FolderIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import CustomTreeItem from '../CustomTreeItem';
import ActionMenu from '../../menu/ActionMenu';
import { breakpoints } from '../../../theme/theme';
import styles from '../../../styles/folderDrawerView/index.module.scss';
import hasChildren from '../helperfunctions';
import { useSortableRow } from './useSortableRow';
import { shouldDimRow } from './dragFeedback';

export const ModuleName = ({ name, color, fontWeight = 500 }) => {
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const textRef = React.useRef(null);

  React.useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [name]);

  return (
    <Tooltip title={isOverflowing ? name : ''} arrow placement="top">
      <Typography
        ref={textRef}
        variant="contentSmall"
        className={`${styles.moduleNameContainer} ellipsis-container`}
        sx={{ fontWeight, color }}
      >
        {name}
      </Typography>
    </Tooltip>
  );
};

function getColors({ isExpanded, isHovered, level }) {
  if (isExpanded) {
    return {
      textColor: '#1e3a8a',
      countBg: '#dbeafe',
      countText: '#1d4ed8',
      countBorder: '#93c5fd',
      iconBg: '#dbeafe',
      iconColor: '#2563eb',
    };
  }
  if (isHovered) {
    return {
      textColor: '#0f172a',
      countBg: '#e2e8f0',
      countText: '#475569',
      iconBg: '#dbeafe',
      iconColor: '#2563eb',
    };
  }
  return {
    textColor: level > 1 ? '#374151' : '#1e293b',
    countBg: '#f1f5f9',
    countText: '#64748b',
    iconBg: '#f1f5f9',
    iconColor: '#64748b',
  };
}

function ModuleTreeItemShell({
  node,
  level,
  expanded,
  setExpanded,
  hoveredNodeId,
  setHoveredNodeId,
  onLeafNodeClick,
  disableActions,
  rowActionButtons,
  children,
  showDragHandle = false,
  dragHandleProps = {},
  setNodeRef,
  isDragging = false,
  insertEdge = null,
  parentId = null,
  activeId = null,
  activeParentId = null,
  dropFlashId = null,
}) {
  const isLeafNode = !hasChildren(node);
  const isExpanded = expanded.includes(String(node.id));
  const isHovered = hoveredNodeId === node.id;
  const isDimmed = shouldDimRow({
    activeId,
    activeParentId,
    rowId: node.id,
    rowParentId: parentId,
  });
  const isDropFlash = String(dropFlashId) === String(node.id);
  const colors = getColors({ isExpanded, isHovered, level });

  let countValue;
  if (isLeafNode) {
    countValue = node.individualcount;
  } else {
    countValue = isExpanded ? node.individualcount : node.totalcount;
  }

  const insertBorder =
    insertEdge === 'top'
      ? { borderTop: '2px solid #3b82f6' }
      : insertEdge === 'bottom'
        ? { borderBottom: '2px solid #3b82f6' }
        : {};

  return (
    <CustomTreeItem
      id={node.id}
      itemId={String(node.id)}
      level={level}
      isExpanded={isExpanded}
      className={styles[`child-${level}`]}
      sx={{
        ...(isDropFlash ? {
          '& > .MuiTreeItem-content': {
            backgroundColor: '#dbeafe',
            boxShadow: 'inset 0 0 0 1px #93c5fd',
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          },
        } : {}),
      }}
      label={
        <Box
          ref={setNodeRef}
          onMouseEnter={() => setHoveredNodeId(node.id)}
          onMouseLeave={() => setHoveredNodeId(null)}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            minHeight: level > 1 ? '20px' : '18px',
            gap: '6px',
            position: 'relative',
            opacity: isDragging ? 0.4 : isDimmed ? 0.38 : 1,
            transition: 'opacity 0.15s ease',
            ...insertBorder,
            [`@media (min-width:${breakpoints.xl}px)`]: {
              py: '3px',
            },
          }}
          onClick={(e) => {
            if (
              e.target.closest('.module-action-menu') === null &&
              e.target.closest('.module-drag-handle') === null
            ) {
              onLeafNodeClick(node.id, node.name);
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflow: 'hidden',
              flex: 1,
            }}
          >
            <IconButton
              disableRipple
              sx={{
                padding: 0,
                cursor: !isLeafNode ? 'pointer' : 'default',
              }}
              onClick={(e) => {
                if (!isLeafNode) {
                  e.stopPropagation();
                  if (isExpanded) {
                    setExpanded(expanded.filter((id) => id !== String(node.id)));
                  } else {
                    setExpanded([...expanded, String(node.id)]);
                  }
                }
              }}
            >
              {!isLeafNode ? (
                isExpanded ? (
                  <ChevronDownIcon className="icon-small" style={{ color: '#2563eb' }} />
                ) : (
                  <ChevronRightIcon
                    className="icon-small"
                    style={{ color: isHovered ? '#2563eb' : '#64748b' }}
                  />
                )
              ) : (
                <span className={level > 2 ? 'icon-x-small' : 'icon-small'} />
              )}
            </IconButton>

            <Box
              className="icon-cover-small module-drag-handle"
              sx={{
                backgroundColor: showDragHandle ? '#dbeafe' : colors.iconBg,
                border: showDragHandle
                  ? '1px solid #93c5fd'
                  : isHovered && level > 1
                    ? '1px solid #e2e8f0'
                    : 'none',
                cursor: Object.keys(dragHandleProps).length ? 'grab' : 'default',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
                boxShadow: showDragHandle ? '0 1px 2px rgba(37, 99, 235, 0.15)' : 'none',
                transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
              }}
              {...dragHandleProps}
              onClick={(e) => {
                if (Object.keys(dragHandleProps).length) e.stopPropagation();
              }}
            >
              {showDragHandle ? (
                <ArrowsUpDownIcon
                  className="icon-small"
                  style={{ color: '#2563eb' }}
                  data-testid="module-drag-handle"
                />
              ) : level > 1 ? (
                <DocumentIcon className="icon-small" style={{ color: colors.iconColor }} />
              ) : (
                <FolderIcon className="icon-small" style={{ color: colors.iconColor }} />
              )}
            </Box>

            <ModuleName
              name={node.name}
              color={isHovered && level > 1 ? '#0f172a' : colors.textColor}
              fontWeight={level > 1 ? 400 : 500}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              variant="contentSmallSemiBold"
              style={{
                marginLeft: 8,
                color:
                  countValue === 0
                    ? '#dc2626'
                    : isHovered && level > 1
                      ? '#475569'
                      : colors.countText,
                backgroundColor: colors.countBg,
                padding: '2px 6px',
                borderRadius: '9999px',
                border: isExpanded ? `1px solid ${colors.countBorder}` : 'none',
              }}
            >
              {countValue ?? 0}
            </Typography>
            {!disableActions && (
              <Box
                className="module-action-menu"
                sx={{
                  opacity: hoveredNodeId === node.id ? 1 : 0,
                  transition: 'opacity 0.2s',
                  marginLeft: 1,
                  pointerEvents: hoveredNodeId === node.id ? 'auto' : 'none',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <ActionMenu menuItems={rowActionButtons(node)} rowData={node} />
              </Box>
            )}
          </Box>
        </Box>
      }
    >
      {children}
    </CustomTreeItem>
  );
}

/** Plain node — no dnd-kit hooks (used when reorderable is false). */
export function ModuleTreeNode(props) {
  return <ModuleTreeItemShell {...props} showDragHandle={false} />;
}

/** Sortable node — must render under SortableContext. */
export function SortableModuleTreeNode({
  parentId = null,
  siblingIds = [],
  showDragHandle = false,
  ...props
}) {
  const { setNodeRef, dragHandleProps, isDragging, insertEdge } = useSortableRow({
    id: props.node.id,
    parentId,
    label: props.node.name,
    siblingIds,
    disabled: false,
  });

  return (
    <ModuleTreeItemShell
      {...props}
      parentId={parentId}
      showDragHandle={showDragHandle}
      dragHandleProps={dragHandleProps}
      setNodeRef={setNodeRef}
      isDragging={isDragging}
      insertEdge={insertEdge}
    />
  );
}
